<template>
  <v-dialog
    v-if="featureEnabled"
    :value="open"
    width="400"
    @click:outside="handleClose"
    @keydown.esc="handleClose"
  >
    <v-card class="aosd-card pa-6 background-pattern-icon" data-testid="add-onboarding-step-dialog">
      <div class="d-flex justify-end mb-4">
        <v-btn
          icon
          color="grey"
          aria-label="Close"
          data-testid="aosd-close"
          @click="handleClose"
        >
          <v-icon>mdi-close</v-icon>
        </v-btn>
      </div>

      <h2 id="aosd-title" class="aosd-title" data-testid="aosd-title">Add onboarding step</h2>

      <div class="aosd-field">
        <label class="aosd-label" for="aosd-name-input" data-testid="aosd-name-label">
          Step name
          <span class="aosd-required" aria-hidden="true">*</span>
        </label>
        <input
          id="aosd-name-input"
          v-model="stepNameModel"
          class="aosd-input"
          :class="{ 'aosd-input--filled': hasStepName }"
          type="text"
          placeholder="Enter step name"
          :maxlength="STEP_NAME_MAX_LENGTH"
          autocomplete="off"
          required
          :aria-required="true"
          data-testid="aosd-name-input"
        />
        <p class="aosd-counter" data-testid="aosd-counter" aria-live="polite">
          {{ stepNameModel.length }}/{{ STEP_NAME_MAX_LENGTH }}
        </p>
      </div>

      <div class="aosd-footer">
        <button
          type="button"
          class="aosd-btn aosd-btn--primary"
          :class="{ 'aosd-btn--disabled': !hasStepName }"
          :disabled="!hasStepName"
          data-testid="aosd-save"
          @click="handleSave"
        >
          Save as Draft
        </button>
      </div>
    </v-card>
  </v-dialog>
</template>

<script setup>
import { computed, getCurrentInstance, onMounted, ref, watch } from "vue";

import { LD_FLAG_IDEA_MDP_600 } from "@components/constants/launchDarklyFlagKeys";

/**
 * ER-11477 — AddOnboardingStepDialog (Employee Onboarding Program step creation).
 *
 * Untitled-UI modal dialog for adding a new onboarding step, opened from an
 * Onboarding steps list or wizard. Contains a required "Step name" text input
 * (placeholder "Enter step name") with a live character counter and a single
 * "Save as Draft" action that stays disabled/grey until the Step name has a
 * value, then enables in purple. The close X (and Escape / click-outside)
 * dismisses the dialog without creating a step.
 *
 * Self-contained presentational component: the parent owns the `open` state,
 * optionally seeds the input via `stepName`, and receives the trimmed name on
 * `save` or a `close` signal when the dialog is dismissed. No API calls inside
 * the component — the owning screen creates the step (in Draft status) when it
 * receives `save`.
 *
 * Shipped behind the linked idea flag (idea-MDP-600, "Employee Onboarding
 * Program — global step-based onboarding"). Renders by default; set the flag to
 * false to keep the dialog closed until a screen wires it.
 */
const props = defineProps({
  /** Boolean that controls dialog visibility. */
  open: {
    type: Boolean,
    default: false,
  },
  /** Initial value seeded into the Step name input when the dialog opens. */
  stepName: {
    type: String,
    default: "",
  },
});

const emit = defineEmits(["save", "close"]);

const STEP_NAME_MAX_LENGTH = 50;

const featureEnabled = ref(false);
const stepNameModel = ref(props.stepName);

const proxy = getCurrentInstance()?.proxy;

const hasStepName = computed(() => stepNameModel.value.trim().length > 0);

watch(
  () => props.open,
  (isOpen) => {
    // Reset the input to the supplied value on every open so a stale entry never
    // leaks across opens.
    if (isOpen) {
      stepNameModel.value = props.stepName;
    }
  }
);

function handleSave() {
  if (!hasStepName.value) {
    return;
  }
  emit("save", stepNameModel.value.trim());
}

function handleClose() {
  emit("close");
}

onMounted(async () => {
  try {
    featureEnabled.value = (await proxy?.$getFlag?.(LD_FLAG_IDEA_MDP_600, true)) !== false;
  } catch {
    // Kill-switch lookup failures never lock the dialog shut.
    featureEnabled.value = true;
  }
});
</script>

<style scoped>
.aosd-card {
  background-color: #ffffff;
  border: 1px solid #e4e7ec;
  /* Vuetify's v-card radius must be flattened so the Untitled UI card keeps its shape. */
  border-radius: 12px !important;
}

.aosd-title {
  margin: 0 0 20px;
  color: #101828;
  font-size: 18px;
  font-weight: 600;
  line-height: 28px;
}

.aosd-field {
  display: flex;
  flex-direction: column;
  gap: 6px;
}

.aosd-label {
  display: block;
  font-size: 14px;
  font-weight: 500;
  line-height: 20px;
  color: #344054;
}

.aosd-required {
  color: #f04438;
}

.aosd-input {
  box-sizing: border-box;
  width: 100%;
  height: 44px;
  padding: 10px 14px;
  background: #ffffff;
  border: 1px solid #d0d5dd;
  border-radius: 8px;
  font-family: inherit;
  font-size: 16px;
  line-height: 24px;
  color: #101828;
  box-shadow: 0 1px 2px rgba(16, 24, 40, 0.05);
}

.aosd-input::placeholder {
  color: #667085;
}

.aosd-input--filled {
  border-color: #7f56d9;
}

.aosd-counter {
  margin: 0;
  font-size: 12px;
  line-height: 18px;
  color: #667085;
  text-align: right;
}

.aosd-footer {
  display: flex;
  justify-content: flex-end;
  margin-top: 24px;
}

.aosd-btn {
  box-sizing: border-box;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  height: 44px;
  min-width: 64px;
  padding: 0 16px;
  border-radius: 8px;
  font-family: inherit;
  font-size: 14px;
  font-weight: 600;
  line-height: 20px;
  cursor: pointer;
}

.aosd-btn--primary {
  background: #7f56d9;
  border: 1px solid #7f56d9;
  color: #ffffff;
  box-shadow: 0 1px 2px rgba(16, 24, 40, 0.05);
}

/* Save (disabled/empty) state: #F2F4F7 background, no border, #98A2B3 text. */
.aosd-btn--disabled,
.aosd-btn:disabled {
  background: #f2f4f7;
  border: none;
  color: #98a2b3;
  box-shadow: none;
  cursor: not-allowed;
}

.aosd-input:focus-visible,
.aosd-btn:focus-visible {
  outline: 2px solid #7f56d9;
  outline-offset: 1px;
}

.background-pattern-icon {
  background-image: url("@components/assets/backgrounds/background_popup_icon.svg");
  background-size: 216px 216px;
  background-position: 0px 0px;
}
</style>
